/**
 * Normalized mode for the parent component selector.
 *
 * - `hidden`:   Do not render the parent component selector.
 * - `optional`: Render the selector; user may leave it empty.
 * - `required`: Render the selector; user must select a value before submission.
 *
 * Configured via `ui:options.showParentComponent` in scaffolder templates:
 *   - `false` → hidden
 *   - `true`  → optional  (backward-compatible: existing templates keep working)
 *   - `"optional"` → optional
 *   - `"required"` → required
 *
 * Invalid values log a warning and fall back to `hidden`.
 */
export type ParentComponentMode = 'hidden' | 'optional' | 'required';

const VALID_STRING_MODES = new Set<string>(['optional', 'required']);

/**
 * Normalize the raw `showParentComponent` option from a scaffolder template
 * into a `ParentComponentMode`.
 */
export function normalizeParentComponentMode(
  raw: unknown,
): ParentComponentMode {
  if (raw === true) return 'optional';
  if (raw === false || raw === undefined || raw === null) return 'hidden';

  if (typeof raw === 'string') {
    const lower = raw.toLowerCase().trim();
    if (VALID_STRING_MODES.has(lower)) {
      return lower as ParentComponentMode;
    }
  }

  // eslint-disable-next-line no-console
  console.warn(
    `AsyncProductAndServiceSelect: invalid showParentComponent value: ${JSON.stringify(raw)}. ` +
      `Expected false, true, "optional", or "required". Falling back to "hidden".`,
  );
  return 'hidden';
}
